import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

type ThemeMode = 'light' | 'dark' | 'system'

type ThemeState = {
  primaryColor: string
  headerBg: string
  asideBg: string
  asideText: string
  themeMode: ThemeMode
  darkMode: boolean
  weakMode: boolean
  grayMode: boolean
  asideCollapsed: boolean
  showTabs: boolean
  layoutMode: 'sidebar' | 'mix' | 'top'
  navTheme: 'dark' | 'light'
  contentWidth: 'fluid' | 'fixed'
  fixedHeader: boolean
  showLogo: boolean
  showBreadcrumb: boolean
  showBreadcrumbIcon: boolean
  showFooter: boolean
  componentSize: 'default' | 'large' | 'small'
  radiusScale: number
  dynamicTitle: boolean
  watermarkEnabled: boolean
  watermarkText: string
  pageProgressEnabled: boolean
  pageLoadingEnabled: boolean
  pageTransitionEnabled: boolean
  pageTransitionName: 'fade' | 'scale' | 'slide-left' | 'slide-right' | 'zoom-fade'
}

const DEFAULT_THEME_STATE: ThemeState = {
  primaryColor: '#0052D9',
  headerBg: '#ffffff',
  asideBg: '#1f2937',
  asideText: 'rgba(255, 255, 255, 0.85)',
  themeMode: 'light',
  darkMode: false,
  weakMode: false,
  grayMode: false,
  asideCollapsed: false,
  showTabs: true,
  layoutMode: 'sidebar',
  navTheme: 'light',
  contentWidth: 'fluid',
  fixedHeader: true,
  showLogo: true,
  showBreadcrumb: true,
  showBreadcrumbIcon: false,
  showFooter: false,
  componentSize: 'default',
  radiusScale: 0.375,
  dynamicTitle: true,
  watermarkEnabled: false,
  watermarkText: 'SMART·Admin',
  pageProgressEnabled: true,
  pageLoadingEnabled: false,
  pageTransitionEnabled: true,
  pageTransitionName: 'fade',
}

const STORAGE_KEY = 'smart.theme'
const MAX_RADIUS_PX = 16
const STATUS_COLOR_MAP = {
  success: '#13C2C2',
  warning: '#FAAD14',
  danger: '#E34D59',
  info: '#0052D9'
} as const
const STATUS_TOKEN_MAP = {
  success: '--td-success-color',
  warning: '--td-warning-color',
  danger: '--td-error-color',
  info: '--td-brand-color'
} as const
const systemDarkMedia =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null
let systemDarkListenerBound = false

function normalizeHex(color: string): string | null {
  if (!color) return null
  const hex = color.trim().replace('#', '')
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return hex
      .split('')
      .map((c) => c + c)
      .join('')
      .toUpperCase()
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return hex.toUpperCase()
  }
  return null
}

function hexToRgb(color: string): [number, number, number] | null {
  const normalized = normalizeHex(color)
  if (!normalized) return null
  const intVal = parseInt(normalized, 16)
  const r = (intVal >> 16) & 255
  const g = (intVal >> 8) & 255
  const b = intVal & 255
  return [r, g, b]
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)))
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`
}

function mixWithWhite(color: string, ratio: number): string | null {
  const rgb = hexToRgb(color)
  if (!rgb) return null
  const [r, g, b] = rgb
  return rgbToHex(r + (255 - r) * ratio, g + (255 - g) * ratio, b + (255 - b) * ratio)
}

function mixWithBlack(color: string, ratio: number): string | null {
  const rgb = hexToRgb(color)
  if (!rgb) return null
  const [r, g, b] = rgb
  return rgbToHex(r * (1 - ratio), g * (1 - ratio), b * (1 - ratio))
}

function buildPalette(color: string): Record<string, string> {
  const palette: Record<string, string> = {}
  if (!normalizeHex(color)) {
    return palette
  }
  const light3 = mixWithWhite(color, 0.3)
  const light5 = mixWithWhite(color, 0.5)
  const light7 = mixWithWhite(color, 0.7)
  const light8 = mixWithWhite(color, 0.8)
  const light9 = mixWithWhite(color, 0.9)
  const dark2 = mixWithBlack(color, 0.2)
  if (light3) palette['-light-3'] = light3
  if (light5) palette['-light-5'] = light5
  if (light7) palette['-light-7'] = light7
  if (light8) palette['-light-8'] = light8
  if (light9) palette['-light-9'] = light9
  if (dark2) palette['-dark-2'] = dark2
  return palette
}

function resolveDarkMode(mode: ThemeMode): boolean {
  if (mode === 'system') {
    return systemDarkMedia?.matches ?? false
  }
  return mode === 'dark'
}

function clampRadiusScale(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_THEME_STATE.radiusScale
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

function applyCssVars(state: ThemeState) {
  const root = document.documentElement
  root.style.setProperty('--smart-primary', state.primaryColor)
  root.style.setProperty('--td-brand-color', state.primaryColor)
  root.style.setProperty('--smart-header-bg', state.headerBg)
  root.style.setProperty('--smart-aside-bg', state.asideBg)
  root.style.setProperty('--smart-aside-color', state.asideText)
  const primaryPalette = buildPalette(state.primaryColor)
  Object.entries(primaryPalette).forEach(([suffix, value]) =>
    root.style.setProperty(`--td-brand-color${suffix}`, value)
  )

  const radiusScale = clampRadiusScale(state.radiusScale)
  const baseRadius = Math.round(MAX_RADIUS_PX * radiusScale * 10) / 10
  const basePx = `${baseRadius}px`
  const smallPx = `${Math.max(baseRadius - 2, 0)}px`
  const roundPx = `${Math.max(baseRadius + 4, 0)}px`
  const circlePx = `${Math.max(baseRadius + 50, 0)}px`

  root.style.setProperty('--smart-radius-base', basePx)
  root.style.setProperty('--td-radius-default', basePx)
  root.style.setProperty('--td-radius-small', smallPx)
  root.style.setProperty('--td-radius-medium', roundPx)
  root.style.setProperty('--td-radius-circle', circlePx)

  const isDark = state.darkMode
  const pageBg = isDark ? '#0f172a' : '#f5f7fb'
  const surfaceBg = isDark ? '#152238' : '#ffffff'
  const overlayBg = isDark ? '#1f2937' : '#ffffff'
  const overlayMask = isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(60, 64, 67, 0.4)'
  const textPrimary = isDark ? 'rgba(255, 255, 255, 0.92)' : '#303133'
  const textRegular = isDark ? 'rgba(255, 255, 255, 0.72)' : '#606266'
  const borderColor = isDark ? 'rgba(148, 163, 184, 0.28)' : '#dcdfe6'
  const borderColorLight = isDark ? 'rgba(148, 163, 184, 0.18)' : '#e4e7ed'
  const fillLight = isDark ? 'rgba(148, 163, 184, 0.12)' : '#f5f7fa'
  const scrollbarThumb = isDark ? 'rgba(148, 163, 184, 0.38)' : 'rgba(148, 163, 184, 0.45)'
  const headerText = state.navTheme === 'dark' ? '#FFFFFF' : textPrimary
  const headerIcon = state.navTheme === 'dark' ? '#FFFFFF' : textPrimary
  const headerHover = state.navTheme === 'dark' ? 'rgba(255, 255, 255, 0.24)' : 'rgba(64, 158, 255, 0.12)'

  root.style.setProperty('--smart-page-bg', pageBg)
  root.style.setProperty('--smart-surface-bg', surfaceBg)
  root.style.setProperty('--smart-text-primary', textPrimary)
  root.style.setProperty('--smart-text-regular', textRegular)
  root.style.setProperty('--smart-border-color', borderColor)
  root.style.setProperty('--smart-overlay-mask', overlayMask)
  root.style.setProperty('--smart-scrollbar-thumb', scrollbarThumb)
  root.style.setProperty('--smart-header-text', headerText)
  root.style.setProperty('--smart-header-icon', headerIcon)
  root.style.setProperty('--smart-header-hover', headerHover)

  root.style.setProperty('--td-bg-color-container', surfaceBg)
  root.style.setProperty('--td-bg-color-page', pageBg)
  root.style.setProperty('--td-bg-color-container', overlayBg)
  root.style.setProperty('--td-text-color-primary', textPrimary)
  root.style.setProperty('--td-text-color-secondary', textRegular)
  root.style.setProperty('--td-border-level-2-color', borderColor)
  root.style.setProperty('--td-border-level-1-color', borderColorLight)
  root.style.setProperty('--td-bg-color-secondarycontainer', fillLight)

  Object.entries(STATUS_COLOR_MAP).forEach(([key, value]) => {
    const token = STATUS_TOKEN_MAP[key as keyof typeof STATUS_TOKEN_MAP]
    root.style.setProperty(token, value)
    const palette = buildPalette(value)
    Object.entries(palette).forEach(([suffix, colorValue]) => {
      root.style.setProperty(`${token}${suffix}`, colorValue)
    })
  })

  root.style.setProperty('color-scheme', isDark ? 'dark' : 'light')

  root.classList.toggle('dark', state.darkMode)
  root.classList.toggle('theme-weak', state.weakMode)
  root.classList.toggle('theme-gray', state.grayMode)
  root.classList.toggle('layout-fixed-header', state.fixedHeader)
  root.classList.toggle('layout-fixed-content', state.contentWidth === 'fixed')
  root.classList.toggle('layout-hide-logo', !state.showLogo)
  root.classList.toggle('layout-hide-footer', !state.showFooter)
  root.setAttribute('data-layout-mode', state.layoutMode)
  root.setAttribute('data-nav-theme', state.navTheme)
  root.setAttribute('data-content-width', state.contentWidth)
  root.setAttribute('data-td-size', state.componentSize)
  root.setAttribute('data-theme-mode', state.themeMode)
  root.classList.toggle('layout-page-progress', state.pageProgressEnabled)
  root.classList.toggle('layout-page-loading', state.pageLoadingEnabled)
  root.classList.toggle('layout-page-transition', state.pageTransitionEnabled)
  root.setAttribute('data-page-transition', state.pageTransitionName)

  if (typeof document !== 'undefined') {
    document.body.style.backgroundColor = pageBg
    document.body.style.color = textPrimary
  }
}

function loadState(): ThemeState {
  const fallback: ThemeState = { ...DEFAULT_THEME_STATE }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ThemeState>
      return {
        ...fallback,
        ...parsed,
        themeMode: (parsed.themeMode as ThemeMode) ?? fallback.themeMode,
        radiusScale: clampRadiusScale(Number(parsed.radiusScale ?? fallback.radiusScale)),
        componentSize:
          (parsed.componentSize as ThemeState['componentSize']) ?? fallback.componentSize,
      }
    }
  } catch {}
  return fallback
}

export const useThemeStore = defineStore('theme', () => {
  const state = ref<ThemeState>(loadState())

  function syncDarkMode(mode?: ThemeMode) {
    state.value.darkMode = resolveDarkMode(mode ?? state.value.themeMode)
  }

  function applyTheme() {
    applyCssVars(state.value)
  }

  function ensureNavPalette() {
    if (state.value.navTheme === 'dark') {
      if (state.value.darkMode) {
        state.value.headerBg = '#0b1220'
        state.value.asideBg = '#0b1724'
      } else {
        state.value.headerBg = '#1f1f2f'
        state.value.asideBg = '#1f2937'
      }
      state.value.asideText = 'rgba(255, 255, 255, 0.85)'
    } else {
      if (state.value.darkMode) {
        state.value.headerBg = '#1f2533'
        state.value.asideBg = '#1f2533'
        state.value.asideText = 'rgba(255, 255, 255, 0.85)'
      } else {
        state.value.headerBg = '#ffffff'
        state.value.asideBg = '#ffffff'
        state.value.asideText = '#1f2937'
      }
    }
  }

  function setThemeMode(mode: ThemeMode) {
    state.value.themeMode = mode
    syncDarkMode(mode)
    ensureNavPalette()
    applyTheme()
  }

  function setPrimary(color: string) {
    state.value.primaryColor = color
    applyTheme()
  }
  function setHeaderBg(color: string) {
    state.value.headerBg = color
    applyTheme()
  }
  function setAsideBg(color: string) {
    state.value.asideBg = color
    applyTheme()
  }
  function setAsideText(color: string) {
    state.value.asideText = color
    applyTheme()
  }
  function applyNavTheme(theme: 'dark' | 'light') {
    state.value.navTheme = theme
    ensureNavPalette()
    applyTheme()
  }
  function setLayoutMode(mode: ThemeState['layoutMode']) {
    state.value.layoutMode = mode
    if (mode === 'top') {
      state.value.asideCollapsed = false
    }
    applyTheme()
  }
  function setContentWidth(mode: ThemeState['contentWidth']) {
    state.value.contentWidth = mode
    applyTheme()
  }
  function toggleDark(v?: boolean) {
    if (typeof v === 'boolean') {
      setThemeMode(v ? 'dark' : 'light')
      return
    }
    if (state.value.themeMode === 'system') {
      setThemeMode(state.value.darkMode ? 'light' : 'dark')
    } else {
      setThemeMode(state.value.themeMode === 'dark' ? 'light' : 'dark')
    }
  }
  function toggleWeak(v?: boolean) {
    state.value.weakMode = typeof v === 'boolean' ? v : !state.value.weakMode
    applyTheme()
  }
  function toggleGray(v?: boolean) {
    state.value.grayMode = typeof v === 'boolean' ? v : !state.value.grayMode
    applyTheme()
  }
  function toggleAside(v?: boolean) {
    state.value.asideCollapsed = typeof v === 'boolean' ? v : !state.value.asideCollapsed
    applyTheme()
  }
  function toggleTabs(v?: boolean) {
    state.value.showTabs = typeof v === 'boolean' ? v : !state.value.showTabs
  }
  function toggleFixedHeader(v?: boolean) {
    state.value.fixedHeader = typeof v === 'boolean' ? v : !state.value.fixedHeader
    applyTheme()
  }
  function toggleLogo(v?: boolean) {
    state.value.showLogo = typeof v === 'boolean' ? v : !state.value.showLogo
    applyTheme()
  }
  function toggleBreadcrumb(v?: boolean) {
    state.value.showBreadcrumb = typeof v === 'boolean' ? v : !state.value.showBreadcrumb
  }
  function toggleBreadcrumbIcon(v?: boolean) {
    state.value.showBreadcrumbIcon = typeof v === 'boolean' ? v : !state.value.showBreadcrumbIcon
  }
  function toggleFooter(v?: boolean) {
    state.value.showFooter = typeof v === 'boolean' ? v : !state.value.showFooter
    applyTheme()
  }
  function setComponentSize(size: ThemeState['componentSize']) {
    state.value.componentSize = size
    applyTheme()
  }
  function setRadiusScale(scale: number) {
    state.value.radiusScale = clampRadiusScale(scale)
    applyTheme()
  }
  function toggleDynamicTitle(v?: boolean) {
    state.value.dynamicTitle = typeof v === 'boolean' ? v : !state.value.dynamicTitle
  }
  function toggleWatermark(v?: boolean) {
    state.value.watermarkEnabled = typeof v === 'boolean' ? v : !state.value.watermarkEnabled
    applyTheme()
  }
  function setWatermarkText(text: string) {
    state.value.watermarkText = text
  }
  function togglePageProgress(v?: boolean) {
    state.value.pageProgressEnabled = typeof v === 'boolean' ? v : !state.value.pageProgressEnabled
    applyTheme()
  }
  function togglePageLoading(v?: boolean) {
    state.value.pageLoadingEnabled = typeof v === 'boolean' ? v : !state.value.pageLoadingEnabled
    applyTheme()
  }
  function togglePageTransition(v?: boolean) {
    state.value.pageTransitionEnabled =
      typeof v === 'boolean' ? v : !state.value.pageTransitionEnabled
    applyTheme()
  }
  function setPageTransition(name: ThemeState['pageTransitionName']) {
    state.value.pageTransitionName = name
    applyTheme()
  }
  function resetTheme() {
    state.value = { ...DEFAULT_THEME_STATE }
    syncDarkMode()
    ensureNavPalette()
    applyTheme()
  }

  function syncFromBrand(theme: Record<string, string> | undefined | null) {
    if (!theme) return
    const norm = (key: string) => key.replace(/[^a-z0-9-]/gi, '-').toLowerCase()
    let updatedThemeMode = false
    let touchedHeader = false
    let touchedAside = false
    let touchedAsideText = false
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value !== 'string') return
      switch (norm(key)) {
        case 'primary-color':
          state.value.primaryColor = value
          break
        case 'header-bg':
          state.value.headerBg = value
          touchedHeader = true
          break
        case 'aside-bg':
          state.value.asideBg = value
          touchedAside = true
          break
        case 'aside-text':
          state.value.asideText = value
          touchedAsideText = true
          break
        case 'mode-dark':
          state.value.themeMode = value === '1' ? 'dark' : 'light'
          updatedThemeMode = true
          break
        case 'mode-gray':
          state.value.grayMode = value === '1'
          break
        case 'mode-weak':
          state.value.weakMode = value === '1'
          break
        default:
          break
      }
    })

    const brandHeader = state.value.headerBg
    const brandAside = state.value.asideBg
    const brandAsideText = state.value.asideText

    if (updatedThemeMode) {
      syncDarkMode()
    }

    if (!touchedHeader || !touchedAside || !touchedAsideText) {
      ensureNavPalette()
      if (touchedHeader) {
        state.value.headerBg = brandHeader
      }
      if (touchedAside) {
        state.value.asideBg = brandAside
      }
      if (touchedAsideText) {
        state.value.asideText = brandAsideText
      }
    }
    applyTheme()
  }

  syncDarkMode()

  if (systemDarkMedia && !systemDarkListenerBound) {
    const handler = (event: MediaQueryListEvent) => {
      if (state.value.themeMode === 'system') {
        state.value.darkMode = event.matches
        applyTheme()
      }
    }
    if (typeof systemDarkMedia.addEventListener === 'function') {
      systemDarkMedia.addEventListener('change', handler)
    } else if (typeof systemDarkMedia.addListener === 'function') {
      systemDarkMedia.addListener(handler)
    }
    systemDarkListenerBound = true
  }

  watch(
    state,
    (s) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
    },
    { deep: true }
  )

  applyTheme()

  return {
    state,
    applyTheme,
    setThemeMode,
    setPrimary,
    setHeaderBg,
    setAsideBg,
    setAsideText,
    toggleDark,
    toggleWeak,
    toggleGray,
    toggleAside,
    toggleTabs,
    toggleFixedHeader,
    toggleLogo,
    toggleBreadcrumb,
    toggleBreadcrumbIcon,
    toggleFooter,
    setComponentSize,
    setLayoutMode,
    applyNavTheme,
    setContentWidth,
    setRadiusScale,
    toggleDynamicTitle,
    toggleWatermark,
    setWatermarkText,
    togglePageProgress,
    togglePageLoading,
    togglePageTransition,
    setPageTransition,
    resetTheme,
    syncFromBrand,
  }
})
